import { Controller } from "@hotwired/stimulus";
import { createElement } from "lucide";
import { resourceIcon, resourceStyle, resourceOpensNewTab } from "./resource_config";
import linkAttributes from "./link_attributes";
import escapeAttr from "./escape_attr";
import { recordNavigationHref } from "./safe_href";
import { sanitizeCreditCard } from "../../../sanitizers";

// Renders items handed off by an "Add to Mo" button as removable chips in Mo's composer. Items
// are queued in sessionStorage by admin--add-to-mo and rendered here (on connect, or live via the
// context-added event so chips added while the composer is open appear immediately). The queue
// is the source of truth and survives frame remounts; chips are deduped by id so re-draining is
// safe. Each chip writes hidden context[] inputs into the composer form, so on send the items
// reach Mo as context for that turn; the queue and chips are cleared once the turn is submitted.
export default class extends Controller {
  static targets = ["list", "template"];

  connect() {
    this.drainPending = this.#drainPending.bind(this);
    this.clearOnSend = this.#clearOnSend.bind(this);
    this.form = this.element.closest("form");
    window.addEventListener("massimo:context-added", this.drainPending);
    this.form?.addEventListener("turbo:submit-end", this.clearOnSend);
    this.#drainPending();
  }

  disconnect() {
    window.removeEventListener("massimo:context-added", this.drainPending);
    this.form?.removeEventListener("turbo:submit-end", this.clearOnSend);
  }

  remove(event) {
    event.preventDefault();
    const chip = event.currentTarget.closest("[data-context-chip]");
    const id = chip.dataset.contextChipId;
    chip.remove();
    if (id)
      this.#writeQueue(this.#readQueue().filter((queued) => queued.id !== id));
    this.#syncVisibility();
  }

  #drainPending() {
    this.#readQueue().forEach((chip) => this.#addChip(chip));
  }

  #clearOnSend(event) {
    if (!event.detail.success) return;
    this.listTarget.replaceChildren();
    sessionStorage.removeItem("massimo-context");
    this.#syncVisibility();
  }

  #addChip({ id, label, sublabel, type, url, moNote } = {}) {
    if (!label) return;
    [label, sublabel, moNote] = [label, sublabel, moNote].map(sanitizeCreditCard);
    if (
      id &&
      this.listTarget.querySelector(
        `[data-context-chip-id="${CSS.escape(id)}"]`,
      )
    )
      return;
    const fragment = this.templateTarget.content.cloneNode(true);
    const chip = fragment.querySelector("[data-context-chip]");
    if (id) chip.dataset.contextChipId = id;

    chip.classList.add(...resourceStyle(type).split(" ").filter(Boolean));

    const iconTarget = chip.querySelector("[data-context-chip-icon]");
    const icon = resourceIcon(type);
    if (icon) iconTarget.replaceWith(createElement(icon, { width: 14, height: 14 }));
    else iconTarget.remove();

    const text = sublabel ? `${label} · ${sublabel}` : label;
    this.#renderLabel(chip, text, url, type);
    chip.querySelector("[data-context-chip-label-input]").value = label;
    chip.querySelector("[data-context-chip-sublabel-input]").value =
      sublabel || "";
    chip.querySelector("[data-context-chip-type-input]").value = type || "";
    chip.querySelector("[data-context-chip-url-input]").value = url || "";
    chip.querySelector("[data-context-chip-mo-note-input]").value = moNote || "";
    this.listTarget.appendChild(fragment);
    this.#syncVisibility();
  }

  // With a url, the label becomes a link to the record, sharing the open policy (linkAttributes) with
  // resource chips in Mo replies so the embed's new-tab rule is applied consistently.
  #renderLabel(chip, text, url, type) {
    const labelTarget = chip.querySelector("[data-context-chip-label]");
    // Built before massimo_safe_context_url sees them, so a URL that won't persist can't look linked.
    const parsed = recordNavigationHref(url);
    if (!parsed) {
      labelTarget.textContent = text;
      return;
    }
    const inPlace = parsed.origin === window.location.origin && !resourceOpensNewTab(type);
    const attrs = linkAttributes({ inPlace });
    const className = "min-w-0 flex-1 truncate text-sm no-underline hover:underline";
    labelTarget.outerHTML =
      `<a href="${escapeAttr(url)}"${attrs} class="${className}">${escapeAttr(text)}</a>`;
  }

  #readQueue() {
    try {
      return JSON.parse(sessionStorage.getItem("massimo-context") || "[]");
    } catch {
      return [];
    }
  }

  #writeQueue(chips) {
    if (chips.length) {
      sessionStorage.setItem("massimo-context", JSON.stringify(chips));
    } else {
      sessionStorage.removeItem("massimo-context");
    }
  }

  #syncVisibility() {
    this.element.hidden = this.listTarget.children.length === 0;
  }
}
